import { useEffect } from 'react';

/**
 * Very lightweight visitor session tracker.
 * - One per-tab session_id in sessionStorage (survives refresh, dies on tab close)
 * - Heartbeat: fires on mount, then every 60s WHILE page is visible
 * - Emits a final `sendBeacon` on tab close / hide so the last duration is captured
 *
 * The endpoint silently swallows failures — if the backend is down or the user
 * is offline, the app keeps working normally.
 */
const SS_KEY = 'fls_visitor_session_id';
const HEARTBEAT_MS = 60 * 1000;

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try { return crypto.randomUUID(); } catch (_) {}
  }
  return (
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2, 10)
  );
}

function getOrCreateSessionId() {
  try {
    let sid = sessionStorage.getItem(SS_KEY);
    if (!sid) {
      sid = uuid();
      sessionStorage.setItem(SS_KEY, sid);
    }
    return sid;
  } catch (_) {
    // SessionStorage unavailable (e.g., privacy-heavy browser); fall back to memory.
    return uuid();
  }
}

export function useSessionTracker() {
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;

    const sid = getOrCreateSessionId();
    const url = `${base}/api/sessions/beat`;

    const beat = async (event) => {
      try {
        await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sid, event: event || 'ping' }),
          keepalive: true,
        });
      } catch (_) {
        // Silent — tracker must never surface to users.
      }
    };

    // Initial beat on mount
    beat('start');

    // Interval beats while tab visible
    const tick = () => {
      if (document.visibilityState === 'visible') beat('ping');
    };
    const intervalId = setInterval(tick, HEARTBEAT_MS);

    // Final beat on hide / close — sendBeacon fires even during unload
    const finalBeat = () => {
      try {
        const payload = JSON.stringify({ session_id: sid, event: 'end' });
        if (navigator.sendBeacon) {
          const blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
        } else {
          beat('end');
        }
      } catch (_) {}
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') finalBeat();
      else beat('ping');
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', finalBeat);
    window.addEventListener('beforeunload', finalBeat);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', finalBeat);
      window.removeEventListener('beforeunload', finalBeat);
      finalBeat();
    };
  }, []);
}
